/**
 * PlanUpgradeModal
 * Modal for selecting and subscribing to a plan via Stripe Checkout
 */

import React, { useState, useEffect } from 'react';
import { Modal, Button, Card, Row, Col, Spinner, Badge, ToggleButtonGroup, ToggleButton } from 'react-bootstrap';
import { Package, CheckCircle, CreditCard, Bot, Users, HardDrive } from 'lucide-react';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';
import { toast } from 'sonner';

const PlanUpgradeModal = ({ show, onHide, companyId, currentPlanName, hasStripeSubscription }) => {
    const [plans, setPlans] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [checkoutLoading, setCheckoutLoading] = useState(null);
    const [billingCycle, setBillingCycle] = useState('quarterly');

    useEffect(() => {
        if (show) {
            fetchPlans();
        }
    }, [show]);

    const fetchPlans = async () => {
        try {
            setIsLoading(true);
            const res = await axios.get(getApiUrl('plans'), { withCredentials: true });
            // Filter out beta_free — can't subscribe to it
            setPlans((res.data || []).filter(p => p.plan_name !== 'beta_free'));
        } catch (error) {
            console.error('Error fetching plans:', error);
            toast.error('Kunde inte hämta planer');
        } finally {
            setIsLoading(false);
        }
    };

    const getPrice = (plan) => {
        if (billingCycle === 'yearly') {
            return { total: plan.price_yearly_sek, perMonth: Math.round(plan.price_yearly_sek / 12) };
        }
        // Quarterly = 3 months
        const quarterly = plan.price_monthly_sek * 3;
        return { total: quarterly, perMonth: plan.price_monthly_sek };
    };

    const handleSelectPlan = async (planName) => {
        try {
            setCheckoutLoading(planName);

            if (hasStripeSubscription && currentPlanName !== 'beta_free') {
                // Already has a Stripe subscription — use plan change endpoint
                const res = await axios.post(
                    getApiUrl(`${companyId}/subscription/plan/change`),
                    { planName, billingCycle },
                    { withCredentials: true }
                );
                toast.success('Plan ändrad!');
                onHide();
                // Reload to refresh entitlements
                window.location.reload();
            } else {
                // No Stripe subscription — go through checkout
                const res = await axios.post(
                    getApiUrl(`${companyId}/subscription/plan/checkout`),
                    { planName, billingCycle },
                    { withCredentials: true }
                );
                if (res.data?.url) {
                    window.location.href = res.data.url;
                } else {
                    toast.error('Kunde inte skapa betalningssession');
                    setCheckoutLoading(null);
                }
            }
        } catch (error) {
            console.error('Error selecting plan:', error);
            const message = error.response?.data?.error || 'Kunde inte starta betalning';
            toast.error(message);
            setCheckoutLoading(null);
        }
    };

    return (
        <Modal show={show} onHide={onHide} centered size="lg">
            <Modal.Header closeButton>
                <Modal.Title className="d-flex align-items-center">
                    <Package size={20} className="me-2 text-primary" />
                    Välj plan
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {isLoading ? (
                    <div className="text-center py-4">
                        <Spinner animation="border" variant="primary" />
                        <p className="text-muted mt-2">Laddar planer...</p>
                    </div>
                ) : (
                    <>
                        <div className="text-center mb-4">
                            <ToggleButtonGroup
                                type="radio"
                                name="billingCycle"
                                value={billingCycle}
                                onChange={setBillingCycle}
                            >
                                <ToggleButton
                                    id="billing-quarterly"
                                    value="quarterly"
                                    variant={billingCycle === 'quarterly' ? 'primary' : 'outline-primary'}
                                    size="sm"
                                >
                                    Kvartalsvis
                                </ToggleButton>
                                <ToggleButton
                                    id="billing-yearly"
                                    value="yearly"
                                    variant={billingCycle === 'yearly' ? 'primary' : 'outline-primary'}
                                    size="sm"
                                >
                                    Årsvis
                                    <Badge bg="success" className="ms-1" style={{ fontSize: '0.65rem' }}>Spara ~17%</Badge>
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </div>

                        <Row>
                            {plans.map((plan) => {
                                const isCurrentPlan = plan.plan_name === currentPlanName;
                                const price = getPrice(plan);

                                return (
                                    <Col md={4} key={plan.plan_id} className="mb-3">
                                        <Card className={`h-100 ${isCurrentPlan ? 'border-primary' : ''}`}>
                                            {isCurrentPlan && (
                                                <div className="position-absolute top-0 start-50 translate-middle">
                                                    <Badge bg="primary" className="px-2 py-1">Nuvarande</Badge>
                                                </div>
                                            )}
                                            <Card.Body className="d-flex flex-column pt-4 text-center">
                                                <h5 className="mb-3">{plan.display_name}</h5>

                                                <div className="mb-3">
                                                    <h3 className="mb-0" style={{ color: 'var(--bs-primary)' }}>
                                                        {price.total} kr
                                                    </h3>
                                                    <small className="text-muted">
                                                        /{billingCycle === 'yearly' ? 'år' : 'kvartal'}
                                                        {' '}({price.perMonth} kr/mån)
                                                    </small>
                                                </div>

                                                <hr />

                                                <ul className="list-unstyled text-start flex-grow-1">
                                                    <li className="mb-2">
                                                        <Bot size={14} className="me-2 text-primary" />
                                                        <strong>{plan.monthly_ai_credits}</strong> AI-krediter/mån
                                                    </li>
                                                    <li className="mb-2">
                                                        <Users size={14} className="me-2 text-primary" />
                                                        Max <strong>{plan.max_users}</strong> användare
                                                    </li>
                                                    <li className="mb-2">
                                                        <HardDrive size={14} className="me-2 text-primary" />
                                                        <strong>{plan.max_storage_gb} GB</strong> lagring
                                                    </li>
                                                </ul>

                                                <Button
                                                    variant={isCurrentPlan ? 'outline-secondary' : 'primary'}
                                                    className="w-100 mt-auto"
                                                    disabled={isCurrentPlan || !!checkoutLoading}
                                                    onClick={() => handleSelectPlan(plan.plan_name)}
                                                >
                                                    {checkoutLoading === plan.plan_name ? (
                                                        <Spinner animation="border" size="sm" />
                                                    ) : isCurrentPlan ? (
                                                        'Nuvarande plan'
                                                    ) : (
                                                        <>
                                                            <CreditCard size={14} className="me-1" />
                                                            Välj
                                                        </>
                                                    )}
                                                </Button>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                );
                            })}
                        </Row>
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <small className="text-muted me-auto">
                    Betalning hanteras säkert via Stripe
                </small>
                <Button variant="secondary" onClick={onHide}>
                    Avbryt
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default PlanUpgradeModal;
